/**
 * Materials — the one place that decides what each part of the scene is *made of*.
 *
 * A material is a name for a (glyph index, colour slot) pair. Nothing else in the
 * renderer is allowed to pick a glyph and a colour together: terrain, anatomy and
 * the glitch layers all name a material and let this table resolve it. That is what
 * keeps a token coherent — the bird is drawn with the same marks as the ground, and
 * a change here changes every token consistently instead of in six places.
 *
 * Two independent axes are being assigned, and they carry different information:
 *
 *   *Colour slot* carries tone — which thing this is. The palette gate in
 *   `tools/palettecheck.ts` measures exactly the relationships used below, so the
 *   assignments here are the reason those rules exist: breast against plumage mid,
 *   spark against plumage light, accent against background, ink against mid terrain.
 *
 *   *Glyph index* carries texture — how solid this is. Terrain's index is its
 *   elevation band, so the ground's texture is its own height. The creature's is a
 *   flat, high density, because at 32x32 a silhouette only reads if it is a solid
 *   mass. The wing is denser than the body it lies on, so the fold separates by
 *   texture even on palettes where the two tones are close. One material — the
 *   outline — leaves the ramp entirely and is `GLYPH_FILL`, for the reason recorded
 *   on `MAT_OUTLINE`: a mark's contrast depends on the typeface, and an edge may not.
 *
 * Every material resolves to one byte, so the table is a `Uint8Array` and the
 * Solidity mirror is a `bytes` constant indexed by the same numbers.
 */

import { ELEVATION_LEVELS } from "../constants.js";
import { idiv, shr } from "../engine/int.js";
import {
  GLYPH_BLANK,
  GLYPH_EYE,
  GLYPH_FILL,
  GLYPH_PUPIL,
  GLYPH_RAMP,
  cellGlyph,
  cellSlot,
  packCell,
} from "./cell.js";
import {
  SLOT_ACCENT,
  SLOT_BG,
  SLOT_BREAST,
  SLOT_COUNT,
  SLOT_INK,
  SLOT_PLUMAGE_DARK,
  SLOT_PLUMAGE_LIGHT,
  SLOT_PLUMAGE_MID,
  SLOT_SPARK,
  SLOT_TERRAIN,
} from "./palette.js";

/**
 * Glyph density of the creature's body mass.
 *
 * Six of eight. High enough to be a mass rather than a texture, and one step below
 * the outline's ceiling so the two never collide. `puff` dithers this *downward*
 * only (see `MAX_PUFF_DITHER`): a fluffy bird loses ink at its edges and reads as
 * down, a sleek one stays solid. Dithering only one way means the dither can never
 * saturate against the top of the ramp, so high `puff` keeps producing variation
 * instead of flattening out.
 */
export const BODY_DENSITY = 6;

/** Furthest `puff` may pull `BODY_DENSITY` down. Floor is 3, still clearly inked. */
export const MAX_PUFF_DITHER = 3;

/** Nothing drawn. Background shows through. */
export const MAT_EMPTY = 0;
/**
 * Standing water: one flat plane at the biome's water level.
 *
 * It borrows the *top* terrain colour at near-minimum density, which looks backwards
 * and is the only choice that works. The first attempt used the lowest terrain colour
 * — the sea is the low ground, after all — and `tools/paletteprobe.ts` showed why it
 * would have been invisible: band 0 against the background measures a median contrast
 * of 1.13 and a worst of 1.04 across all 128 palette-tint pairs. That is not a palette
 * defect, it is the authored intent, because band 0 is drawn with the lightest ramp
 * step and is supposed to fade the ground in from nothing rather than give the frame a
 * hard floor. Water cannot live there. The top band measures 2.90 worst and 3.79
 * median against the background, so it is guaranteed to read on every palette.
 *
 * Peaks use the same colour and cannot be confused with the sea, because they are
 * drawn at maximum density and water at `WATER_DENSITY`: a peak is a solid block, the
 * sea is a thin sparse line. Value carries substance here and texture carries form,
 * which is the same division the rest of this table uses.
 */
export const MAT_WATER = 1;
/** Land elevation band `b` lives at `MAT_TERRAIN + b`, for b in [0, ELEVATION_LEVELS). */
export const MAT_TERRAIN = 2;

/** The creature's own materials begin here. `isSubject` is this comparison. */
export const MAT_PLUMAGE_DARK = MAT_TERRAIN + ELEVATION_LEVELS;
export const MAT_PLUMAGE_MID = MAT_PLUMAGE_DARK + 1;
export const MAT_PLUMAGE_LIGHT = MAT_PLUMAGE_DARK + 2;
/** The breast patch: the loudest colour in the palette, on the body's front. */
export const MAT_BREAST = MAT_PLUMAGE_DARK + 3;
/** The folded wing. Dark plumage at maximum density, lying over the body's side. */
export const MAT_WING = MAT_PLUMAGE_DARK + 4;
/** Primary feather tips. Ink, because a one-cell line needs the hardest colour. */
export const MAT_PRIMARY = MAT_PLUMAGE_DARK + 5;
/** Tail feathers, including the fan spikes. */
export const MAT_TAIL = MAT_PLUMAGE_DARK + 6;
/** Crest or tuft. Light plumage, one density step down: feathers, not mass. */
export const MAT_CREST = MAT_PLUMAGE_DARK + 7;
/** Beak. Keratin, so it shares `accent` with the legs — the same material twice. */
export const MAT_BEAK = MAT_PLUMAGE_DARK + 8;
/** Legs and feet. */
export const MAT_LEG = MAT_PLUMAGE_DARK + 9;
/** The eye's ring, in the brightest slot the palette owns. */
export const MAT_EYE = MAT_PLUMAGE_DARK + 10;
/** The pupil, drawn over the eye in ink. */
export const MAT_PUPIL = MAT_PLUMAGE_DARK + 11;
/**
 * The silhouette edge, drawn outside the creature over the terrain.
 *
 * A solid `accent` cell, and both halves of that were chosen by measurement in
 * `tools/legibility.ts` after the version that shipped before it was found to be
 * invisible. It used to be `ink` at ramp step 5, on the strength of the comment in
 * `palette.ts` that calls outlines "the only thing separating the bird from the
 * ground". Measured through the glyph, that edge scored **1.01 to 1.05 against the
 * background** on all fourteen dark palettes — no edge at all along the head and back,
 * which is exactly where a bird borders sky. `ink` is the palette's darkest colour and
 * the background is dark; nothing in `palettecheck.ts` covered the pair.
 *
 * *Colour.* Worst-case contrast as a solid cell, over every palette and tint, against
 * the elevation band behind it: accent scores 4.14 / 4.00 / 3.76 / 3.40 / 3.01 / 2.48 /
 * 1.83 on bands 0..6, and it is the only slot with a usable floor on all of them
 * (`ink` bottoms out at 1.01, `spark` and `light` collapse against the pale high
 * bands). Band 7 drops accent to 1.22, and `composecheck.ts` reports band 7 never
 * occurs behind a bird — the peaks are above it.
 *
 * *Treatment.* Accent as a ramp step 5 measured 1.17 worst and at step 7 measured
 * 1.29, because a sparse typeface inks 17.7% of its cell and dilutes the authored
 * contrast toward 1.0. As a fill the same colours measure **1.83 worst**, and 3.34 to
 * 3.60 on the six sparse sets that carry two thirds of the collection's rarity weight.
 * Only coverage 1.0 makes the floor independent of the typeface, so the edge is the one
 * thing in the picture that is not made of the token's own marks.
 */
export const MAT_OUTLINE = MAT_PLUMAGE_DARK + 12;

export const MATERIAL_COUNT = MAT_OUTLINE + 1;

/** First subject material. Everything below this belongs to the landscape. */
export const MAT_FIRST_SUBJECT = MAT_PLUMAGE_DARK;

/** Crest density: one step below the body, so a tuft reads as wispy. */
const CREST_DENSITY = BODY_DENSITY - 1;
/** Keratin and feather tips: the heaviest ramp step. These are all one cell wide. */
const HARD_DENSITY = ELEVATION_LEVELS - 1;
/** Water: the second-lightest ramp step. A surface has no volume to fill. */
const WATER_DENSITY = 1;
/** The terrain slot water borrows. See `MAT_WATER` for the measurement behind it. */
const WATER_SLOT = SLOT_TERRAIN + ELEVATION_LEVELS - 1;

/** Packed cell byte per material, indexed by `MAT_*`. */
export const MATERIAL_CELLS: Uint8Array = buildMaterials();

function buildMaterials(): Uint8Array {
  const t = new Uint8Array(MATERIAL_COUNT);
  t[MAT_EMPTY] = packCell(GLYPH_BLANK, SLOT_BG);
  t[MAT_WATER] = packCell(GLYPH_RAMP + WATER_DENSITY, WATER_SLOT);
  for (let band = 0; band < ELEVATION_LEVELS; band++) {
    t[MAT_TERRAIN + band] = packCell(GLYPH_RAMP + band, SLOT_TERRAIN + band);
  }
  t[MAT_PLUMAGE_DARK] = packCell(BODY_DENSITY, SLOT_PLUMAGE_DARK);
  t[MAT_PLUMAGE_MID] = packCell(BODY_DENSITY, SLOT_PLUMAGE_MID);
  t[MAT_PLUMAGE_LIGHT] = packCell(BODY_DENSITY, SLOT_PLUMAGE_LIGHT);
  t[MAT_BREAST] = packCell(BODY_DENSITY, SLOT_BREAST);
  t[MAT_WING] = packCell(HARD_DENSITY, SLOT_PLUMAGE_DARK);
  t[MAT_PRIMARY] = packCell(HARD_DENSITY, SLOT_INK);
  t[MAT_TAIL] = packCell(BODY_DENSITY, SLOT_PLUMAGE_DARK);
  t[MAT_CREST] = packCell(CREST_DENSITY, SLOT_PLUMAGE_LIGHT);
  t[MAT_BEAK] = packCell(HARD_DENSITY, SLOT_ACCENT);
  t[MAT_LEG] = packCell(HARD_DENSITY, SLOT_ACCENT);
  t[MAT_EYE] = packCell(GLYPH_EYE, SLOT_SPARK);
  t[MAT_PUPIL] = packCell(GLYPH_PUPIL, SLOT_INK);
  t[MAT_OUTLINE] = packCell(GLYPH_FILL, SLOT_ACCENT);
  return t;
}

/** Names for the CLI's ASCII dump and for guard messages. Not on-chain. */
export const MATERIAL_NAMES: readonly string[] = buildNames();

function buildNames(): string[] {
  const n = new Array<string>(MATERIAL_COUNT).fill("");
  n[MAT_EMPTY] = "empty";
  n[MAT_WATER] = "water";
  for (let band = 0; band < ELEVATION_LEVELS; band++) n[MAT_TERRAIN + band] = `terrain${band}`;
  n[MAT_PLUMAGE_DARK] = "plumage-dark";
  n[MAT_PLUMAGE_MID] = "plumage-mid";
  n[MAT_PLUMAGE_LIGHT] = "plumage-light";
  n[MAT_BREAST] = "breast";
  n[MAT_WING] = "wing";
  n[MAT_PRIMARY] = "primary";
  n[MAT_TAIL] = "tail";
  n[MAT_CREST] = "crest";
  n[MAT_BEAK] = "beak";
  n[MAT_LEG] = "leg";
  n[MAT_EYE] = "eye";
  n[MAT_PUPIL] = "pupil";
  n[MAT_OUTLINE] = "outline";
  return n;
}

/** The cell byte a material paints. */
export function materialCell(material: number): number {
  return MATERIAL_CELLS[material] as number;
}

/** Land band `b` as a material. Callers clamp; this does not. */
export function terrainMaterial(band: number): number {
  return MAT_TERRAIN + band;
}

/** True for the creature's materials, false for background, water and land. */
export function isSubject(material: number): boolean {
  return material >= MAT_FIRST_SUBJECT;
}

/**
 * A material's cell with its glyph replaced, keeping the colour.
 *
 * This is how `puff` dithers plumage: the tone stays authored, the texture moves.
 * Anatomy never constructs a cell byte itself, so a material's colour cannot drift
 * away from the table above.
 */
export function shadeCell(material: number, glyph: number): number {
  return packCell(glyph, cellSlot(materialCell(material)));
}

/**
 * Body density after `puff` dithering.
 *
 * `dither` is 0..MAX_PUFF_DITHER and comes from per-cell noise scaled by the
 * archetype's `puff`. Subtracting keeps the result inside the ramp without a clamp
 * on the hot path, which is why `MAX_PUFF_DITHER` is a constant and not a parameter.
 */
export function puffedDensity(dither: number): number {
  const d = dither < 0 ? 0 : dither > MAX_PUFF_DITHER ? MAX_PUFF_DITHER : dither;
  return BODY_DENSITY - d;
}

/**
 * True for the materials drawn at the body's own density — the ones `puff` may dither.
 *
 * Derived from the table rather than listed, so a subject material authored at
 * `BODY_DENSITY` joins the set on its own and one moved off that density leaves it.
 * `isSubject` is not redundant in the test: land band 6 is also drawn at glyph index 6,
 * and the ground's texture is its elevation, which `puff` has no business touching.
 *
 * What this excludes is the point. The wing fold, the primaries, the beak and the legs
 * sit at `HARD_DENSITY` because each is one cell wide and has to hold a hard edge; the
 * crest sits one step *below* the body because a tuft is wispy already. Dithering ink
 * out of any of them removes the part, not its texture.
 */
export function isPlumage(material: number): boolean {
  return isSubject(material) && cellGlyph(materialCell(material)) === BODY_DENSITY;
}

/**
 * Whether a material may seed the outline pass — that is, whether the edge is drawn in
 * the empty cells around it.
 *
 * A rim exists to put a different colour between the bird and the ground. Around a part
 * that is *already* the rim's colour it does none of that: the contrast between the two
 * is 1.00 by construction, so what the viewer sees is not an edge but a part three cells
 * wide. The legs and the beak are keratin and share `accent` with the outline, and both
 * are drawn one cell wide on purpose — a rimmed leg is a post, and a rimmed beak is a
 * wedge. They keep their own separation from the ground instead, which is the same
 * accent-against-terrain measurement `MAT_OUTLINE` records, at 1.29 to 1.83 worst case
 * for a one-cell part at `HARD_DENSITY`.
 *
 * Derived from the table, not listed: repoint the outline at another slot and the set of
 * parts that stop seeding it follows. `validateMaterials` pins today's consequence.
 */
export function seedsOutline(material: number): boolean {
  return cellSlot(materialCell(material)) !== cellSlot(materialCell(MAT_OUTLINE));
}

/**
 * How far `puff` pulls one cell's density down: 0..`MAX_PUFF_DITHER`.
 *
 * `noise` is a per-cell byte from `cellDither`, uncorrelated with the terrain field, and
 * `puff` is the archetype's 0..256 fluff. The product is scaled back down to a byte and
 * then onto the dither's range, which makes `puff` the *amplitude* of the dither and not
 * its probability. That distinction is the whole design: as a probability, a sleek bird
 * would be uniformly solid and a fluffy one solid with a scatter of holes — two states,
 * one of which looks like damage. As an amplitude, a sleek bird's cells all sit at or
 * just under `BODY_DENSITY` and a fluffy one's spread across the full range, so both get
 * a texture and the difference between them is legible over the whole silhouette.
 */
export function puffDither(noise: number, puff: number): number {
  const scaled = shr((noise & 0xff) * puff, 8);
  return idiv(scaled * (MAX_PUFF_DITHER + 1), 256);
}

/** Structural guard invoked by tests. */
export function validateMaterials(): void {
  if (MATERIAL_CELLS.length !== MATERIAL_COUNT) throw new Error("material table is the wrong size");
  if (MATERIAL_NAMES.length !== MATERIAL_COUNT) throw new Error("material names are the wrong size");

  for (let m = 0; m < MATERIAL_COUNT; m++) {
    if (MATERIAL_NAMES[m] === "") throw new Error(`material ${m} has no name`);
    const cell = materialCell(m);
    const glyph = cellGlyph(cell);
    const slot = cellSlot(cell);
    if (slot >= SLOT_COUNT) throw new Error(`${MATERIAL_NAMES[m]}: colour slot ${slot} is out of range`);
    if (glyph !== GLYPH_BLANK && glyph !== GLYPH_EYE && glyph !== GLYPH_PUPIL && glyph !== GLYPH_FILL) {
      if (glyph >= ELEVATION_LEVELS) {
        throw new Error(`${MATERIAL_NAMES[m]}: ramp index ${glyph} overruns the density ramp`);
      }
    }
    if (m !== MAT_EMPTY && glyph === GLYPH_BLANK) {
      throw new Error(`${MATERIAL_NAMES[m]}: draws nothing, so it cannot be a material`);
    }
  }

  // Terrain must stay a straight band -> (ramp index, terrain slot) identity, because
  // both the renderer and the Solidity mirror index it arithmetically.
  for (let band = 0; band < ELEVATION_LEVELS; band++) {
    const cell = materialCell(terrainMaterial(band));
    if (cellGlyph(cell) !== band) throw new Error(`terrain band ${band}: ramp index drifted`);
    if (cellSlot(cell) !== SLOT_TERRAIN + band) throw new Error(`terrain band ${band}: slot drifted`);
  }

  if (puffedDensity(MAX_PUFF_DITHER) < 1) throw new Error("full puff dithers plumage out of existence");
  if (puffedDensity(0) !== BODY_DENSITY) throw new Error("zero puff must leave the body solid");
  if (BODY_DENSITY >= ELEVATION_LEVELS) throw new Error("body density overruns the ramp");

  // The edge is the one guarantee that must not depend on the typeface, so it is the one
  // material allowed off the ramp. See `MAT_OUTLINE` for the measurement: 1.01 as a mark,
  // 1.83 as a fill, worst case over every palette, tint and glyph set.
  const rim = materialCell(MAT_OUTLINE);
  if (cellGlyph(rim) !== GLYPH_FILL) throw new Error("the outline is a mark again, so its contrast is a typeface's opinion");
  if (cellSlot(rim) !== SLOT_ACCENT) throw new Error("the outline left the only slot with a floor against every band");
  for (let m = 0; m < MATERIAL_COUNT; m++) {
    if (m !== MAT_OUTLINE && cellGlyph(materialCell(m)) === GLYPH_FILL) {
      throw new Error(`${MATERIAL_NAMES[m]}: only the outline may be a solid cell`);
    }
  }

  // `seedsOutline` is a slot comparison, so the parts it excuses are a consequence of the
  // table. Pin it: keratin is accent, so keratin is the thing a rim would only thicken.
  const unrimmed: number[] = [];
  for (let m = MAT_FIRST_SUBJECT; m < MATERIAL_COUNT; m++) {
    if (m !== MAT_OUTLINE && !seedsOutline(m)) unrimmed.push(m);
  }
  const keratin = [MAT_BEAK, MAT_LEG];
  const names = (ms: readonly number[]): string => ms.map((m) => MATERIAL_NAMES[m]).join(", ");
  if (unrimmed.length !== keratin.length || keratin.some((m) => !unrimmed.includes(m))) {
    throw new Error(`the outline would skip [${names(unrimmed)}] instead of [${names(keratin)}]`);
  }
  if (!seedsOutline(MAT_PLUMAGE_DARK)) throw new Error("the body no longer seeds its own edge");

  // Water shares the top band's colour, so texture is the only thing separating the
  // sea from a peak. Two ramp steps of gap is the minimum that survives a glyph set
  // as flat as Hexcode (6.4% to 12.2% ink end to end).
  const water = materialCell(MAT_WATER);
  const peak = materialCell(terrainMaterial(ELEVATION_LEVELS - 1));
  if (cellSlot(water) !== cellSlot(peak)) throw new Error("water no longer borrows the peak colour");
  if (cellGlyph(peak) - cellGlyph(water) < 2) {
    throw new Error("water and the highest land are too close in density to tell apart");
  }

  if (MAT_FIRST_SUBJECT !== MAT_PLUMAGE_DARK) throw new Error("subject range no longer starts at plumage");
  for (let m = MAT_FIRST_SUBJECT; m < MATERIAL_COUNT; m++) {
    if (!isSubject(m)) throw new Error(`${MATERIAL_NAMES[m]}: should be a subject material`);
  }
  for (let m = 0; m < MAT_FIRST_SUBJECT; m++) {
    if (isSubject(m)) throw new Error(`${MATERIAL_NAMES[m]}: should be a landscape material`);
  }

  // `isPlumage` is a density test, so the set it selects is a consequence of the table
  // rather than a declaration. Pin the consequence: five body-mass materials, and no
  // land band caught by the same density.
  const plumage: number[] = [];
  for (let m = 0; m < MATERIAL_COUNT; m++) if (isPlumage(m)) plumage.push(m);
  const wanted = [MAT_PLUMAGE_DARK, MAT_PLUMAGE_MID, MAT_PLUMAGE_LIGHT, MAT_BREAST, MAT_TAIL];
  const named = (ms: readonly number[]): string => ms.map((m) => MATERIAL_NAMES[m]).join(", ");
  if (plumage.length !== wanted.length || wanted.some((m) => !plumage.includes(m))) {
    throw new Error(`puff would dither [${named(plumage)}] instead of [${named(wanted)}]`);
  }

  // The dither must stay inside the ramp for every byte of noise at every authored
  // puff, because `puffedDensity` clamps and a clamp that fires is a silent flattening.
  for (const puff of [0, 1, 64, 128, 192, 255, 256]) {
    for (let noise = 0; noise <= 255; noise++) {
      const d = puffDither(noise, puff);
      if (d < 0 || d > MAX_PUFF_DITHER) {
        throw new Error(`puffDither(${noise}, ${puff}) = ${d} is outside 0..${MAX_PUFF_DITHER}`);
      }
    }
  }
  if (puffDither(0, 256) !== 0) throw new Error("silent noise must leave the body solid");
  if (puffDither(255, 0) !== 0) throw new Error("zero puff must leave the body solid");
  if (puffDither(255, 256) !== MAX_PUFF_DITHER) throw new Error("full puff must reach the dither floor");
}
